import { useEffect, useRef } from 'react'
import {
  isLocationFiltersEmpty,
  mergeLocationFilters,
  pruneLocationFiltersAgainstOptions,
  readLocationFilters,
  writeLocationFilters,
  type LocationFilters,
} from '@/shared/lib/locationFiltersSession'

interface OrgOption {
  value: number
  siteId?: number | null
  departmentId?: number | null
}

interface UseHydrateLocationFiltersArgs {
  tenantId: number | null | undefined
  userId: number | null | undefined
  /** True when the current page URL already carries location filter params (URL wins). */
  hasUrlLocationParams: boolean
  /** Location filters currently applied from server props / URL (used to sync → store). */
  currentFilters: LocationFilters
  siteOptions: Array<{ value: number }>
  departmentOptions?: OrgOption[]
  unitOptions?: OrgOption[]
  /**
   * `replace` (default) overwrites the whole store bucket — use on pages that expose
   * Site+Dept+Unit. `merge` updates only keys present on this surface (e.g. monthly
   * reports) so Unit ids from Dashboard/Instruments survive.
   */
  syncMode?: 'replace' | 'merge'
  /** When false, skip sync/hydrate (e.g. duplicate ReportFilters trigger-only instance). */
  enabled?: boolean
  /** Called once when store has filters and the URL does not — navigate to apply them. */
  onHydrate: (filters: LocationFilters) => void
}

/**
 * Store ↔ URL bridge for shared location filters.
 * - URL present → write into store (bookmark / refresh / carried query).
 * - URL absent → prune store against options and hydrate via onHydrate.
 */
export const useHydrateLocationFilters = ({
  tenantId,
  userId,
  hasUrlLocationParams,
  currentFilters,
  siteOptions,
  departmentOptions = [],
  unitOptions = [],
  syncMode = 'replace',
  enabled = true,
  onHydrate,
}: UseHydrateLocationFiltersArgs): void => {
  // Per-user-per-tenant gate so a same-tab user or tenant change re-hydrates from the
  // correct bucket even if the page component stays mounted.
  const ranForScopeRef = useRef<string | null>(null)
  const onHydrateRef = useRef(onHydrate)
  onHydrateRef.current = onHydrate

  useEffect(() => {
    if (!enabled) return
    // Wait for both ids — never key the store under unknown scope.
    if (tenantId == null) return
    const scopeKey = `${tenantId}:${userId ?? 'unknown'}`
    if (ranForScopeRef.current === scopeKey) return
    ranForScopeRef.current = scopeKey

    if (hasUrlLocationParams) {
      if (!isLocationFiltersEmpty(currentFilters)) {
        if (syncMode === 'merge') {
          mergeLocationFilters(tenantId, userId, {
            siteIds: currentFilters.siteIds,
            departmentIds: currentFilters.departmentIds,
          })
        } else {
          writeLocationFilters(tenantId, userId, currentFilters)
        }
      }
      return
    }

    const stored = readLocationFilters(tenantId, userId)
    const pruned = pruneLocationFiltersAgainstOptions(
      stored,
      siteOptions,
      departmentOptions,
      unitOptions,
    )

    // Surfaces without Unit (monthly reports) must not wipe Unit ids from the shared store.
    const effective: LocationFilters =
      syncMode === 'merge' ? { ...pruned, unitIds: stored.unitIds } : pruned

    const hasHydratableLocation =
      effective.siteIds.length > 0 ||
      effective.departmentIds.length > 0 ||
      (syncMode !== 'merge' && effective.unitIds.length > 0)

    if (!hasHydratableLocation) {
      if (!isLocationFiltersEmpty(stored)) {
        // Drop stale inaccessible ids so later pages do not keep re-trying them.
        if (syncMode === 'merge') {
          mergeLocationFilters(tenantId, userId, { siteIds: [], departmentIds: [] })
        } else {
          writeLocationFilters(tenantId, userId, effective)
        }
      }
      return
    }

    onHydrateRef.current(effective)
  }, [
    tenantId,
    userId,
    hasUrlLocationParams,
    currentFilters,
    siteOptions,
    departmentOptions,
    unitOptions,
    syncMode,
    enabled,
  ])
}
